if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.querySelectorAll('[data-gallery-direction]').forEach((button) => {
          button.addEventListener('click', () => {
            const items = [...this.elements.viewer.querySelectorAll('.product__media-item')].filter(
              (item) => !item.classList.contains('product__media-item--variant') || item.classList.contains('is-active')
            );
            if (items.length < 2) return;
            const current = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));
            const next = (current + Number(button.dataset.galleryDirection) + items.length) % items.length;
            this.setActiveMedia(items[next].dataset.mediaId, false, Number(button.dataset.galleryDirection));
          });
        });
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, direction) {
        if (this.slideCleanup) this.slideCleanup();
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        const previousMedia = this.elements.viewer.querySelector('.product__media-item.is-active');
        const animateSlide = !prepend && previousMedia && previousMedia !== activeMedia &&
          this.mql.matches && this.dataset.desktopLayout.includes('thumbnail') &&
          !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const items = [...activeMedia.parentElement.children];
        const slideDirection = typeof direction === 'number' ? direction :
          (items.indexOf(activeMedia) > items.indexOf(previousMedia) ? 1 : -1);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        if (animateSlide) this.animateMediaSlide(previousMedia, activeMedia, slideDirection);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (animateSlide) return;
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      animateMediaSlide(previousMedia, activeMedia, direction) {
        const list = activeMedia.parentElement;
        const previousStyle = previousMedia.getAttribute('style');
        const listStyle = list.getAttribute('style');
        const width = activeMedia.getBoundingClientRect().width;
        list.style.position = 'relative';
        list.style.overflow = 'hidden';
        previousMedia.style.setProperty('display', 'block', 'important');
        previousMedia.style.position = 'absolute';
        previousMedia.style.top = '0';
        previousMedia.style.left = '0';
        previousMedia.style.width = width + 'px';
        previousMedia.style.pointerEvents = 'none';
        const timing = { duration: 300, easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)' };
        const outgoing = previousMedia.animate(
          [{ transform: 'translateX(0)' }, { transform: 'translateX(' + (-direction * 100) + '%)' }], timing
        );
        const incoming = activeMedia.animate(
          [{ transform: 'translateX(' + (direction * 100) + '%)' }, { transform: 'translateX(0)' }], timing
        );
        const cleanup = () => {
          outgoing.cancel();
          incoming.cancel();
          if (previousStyle === null) previousMedia.removeAttribute('style');
          else previousMedia.setAttribute('style', previousStyle);
          if (listStyle === null) list.removeAttribute('style');
          else list.setAttribute('style', listStyle);
          if (this.slideCleanup === cleanup) this.slideCleanup = null;
        };
        this.slideCleanup = cleanup;
        incoming.onfinish = cleanup;
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
